import { useEffect, useMemo, useState } from 'react';
import { Loader } from 'lucide-react';

import { BreadcrumbItemType, ExplorerItem } from '@/types/file-explorer-types';

import IconImg from '@/assets/svg/icon-img.svg?react';

import { useCalculateRowsPerPage } from '@/hooks/use-calculate-rows-per-page';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import {
  INDEX_OBJECT_TYPES,
  ROOT_UUID,
  ROW_HEIGHT_SMALL,
} from '@/constants/global-constants';
import { ExplorerItemType } from '@/constants/table-columns-config';

import { FileExplorer } from './FileExplorer';

interface MoveFileViewProps {
  agreementId: string;
  fileName: string;
}

const MoveFileView = ({ agreementId, fileName }: MoveFileViewProps) => {
  const {
    agreementsState,
    directoriesState,
    insuranceObjectsState,
    getStateByType,
    getBreadcrumbItems,
  } = useFileExplorerStore();

  const { isMoveFileDialogOpen, setIsMoveFileDialogOpen } = useGlobalStore();
  const rowsPerPage = useCalculateRowsPerPage(ROW_HEIGHT_SMALL);

  const rootUUID = useMemo(() => {
    return directoriesState.rootIds.length === 1
      ? directoriesState.entities[directoriesState.rootIds[0]].uuid
      : ROOT_UUID;
  }, [agreementId, directoriesState.entities]);

  const insuranceObject = useMemo(() => {
    const insuranceObjectUuid =
      agreementsState.entities[agreementId]?.insurance_object;
    return insuranceObjectUuid
      ? insuranceObjectsState.entities[insuranceObjectUuid]
      : null;
  }, [agreementId, agreementsState.entities, insuranceObjectsState.entities]);

  const rootEntityType = useMemo(() => {
    return directoriesState.rootIds.length === 1
      ? ExplorerItemType.INSURANCE_OBJECT
      : ExplorerItemType.DIRECTORY;
  }, [directoriesState.rootIds]);

  const [currentUUID, setCurrentUUID] = useState(rootUUID);
  const [currentEntityType, setCurrentEntityType] =
    useState<ExplorerItemType>(rootEntityType);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItemType[]>([]);

  const [items, setItems] = useState<ExplorerItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const fetchItems = async () => {
      const state = await getStateByType(currentEntityType, currentUUID, {
        pageIndex: 0,
        pageSize: rowsPerPage,
      });
      const items = state.rootIds.map((id) => state.entities[id]);
      const breadcrumbs = await getBreadcrumbItems(
        currentEntityType,
        currentUUID,
      );
      setItems(items);
      setBreadcrumbs(breadcrumbs);
      setTotalCount(state.totalCount);
      setHasMore(rowsPerPage === items.length);
    };
    if (isMoveFileDialogOpen) {
      fetchItems();
    }
  }, [currentEntityType, currentUUID, isMoveFileDialogOpen]);

  useEffect(() => {
    setPageIndex(0);
  }, [currentEntityType, currentUUID]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      const nextPageIndex = pageIndex + 1;
      const state = await getStateByType(currentEntityType, currentUUID, {
        pageIndex: nextPageIndex,
        pageSize: rowsPerPage,
      });
      const moreItems = state.rootIds.map((id) => state.entities[id]);

      if (moreItems.length > 0) {
        setItems((prev) => [...prev, ...moreItems]);
        setPageIndex(nextPageIndex);

        const totalLoadedItems = (nextPageIndex + 1) * rowsPerPage;
        setHasMore(
          totalLoadedItems < totalCount && moreItems.length >= rowsPerPage,
        );
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <Dialog open={isMoveFileDialogOpen} onOpenChange={setIsMoveFileDialogOpen}>
      <DialogContent className="flex h-full max-h-[550px] min-w-[650px] flex-col gap-0 p-0">
        <DialogHeader className="flex flex-col space-y-1 border-b border-b-separator px-10 pb-5 pt-[26px]">
          <div className="flex items-center gap-5 pb-2.5">
            <IconImg className="h-[14px] w-[18px] fill-dark-red" />
            <DialogTitle className="text-xl font-medium">
              {fileName}
            </DialogTitle>
          </div>
          <div className="text-base text-primary">
            <span>
              {insuranceObject?.insurance_object_type ===
              INDEX_OBJECT_TYPES.VEHICLE
                ? 'VIN-номер:'
                : 'РНОКПП/ЄДРПОУ:'}
            </span>{' '}
            <span className="font-semibold">{insuranceObject?.name}</span>
          </div>
          <div className="text-base text-primary">
            <span>Місце розташування:</span>{' '}
            <span className="font-semibold">
              {agreementsState.entities[agreementId]?.registration_plate}
            </span>
          </div>
        </DialogHeader>
        <DialogDescription hidden>{fileName}</DialogDescription>
        <div className="flex-1 overflow-y-auto rounded-md">
          <FileExplorer
            items={items}
            currentUUID={currentUUID}
            breadcrumbs={breadcrumbs}
            entityType={currentEntityType}
            setCurrentUUID={setCurrentUUID}
            setCurrentEntityType={setCurrentEntityType}
            isSmall
            isMoveFile
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
          />
        </div>
        {hasMore && (
          <div className="flex justify-center p-2.5">
            <button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              className="hover:text-primary/80 rounded-md px-4 py-2 text-sm font-medium text-primary hover:bg-gray-100 focus:outline-none"
            >
              {isLoadingMore ? (
                <div className="flex items-center gap-2">
                  <Loader className="h-4 w-4 animate-spin-slow"></Loader>
                  <span>Завантаження...</span>
                </div>
              ) : (
                'Завантажити ще'
              )}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MoveFileView;
